import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { accessService } from '../../../services/accessService';
import { UserAccess, AccessType, type MyMesttiOpsAccess } from '../../../types/access';
import { isActiveUserAccess } from '../utils/permissionAccess';

const FOCUS_REFRESH_MIN_INTERVAL_MS = 60_000;

function permissionsAreEqual(left: string[], right: string[]): boolean {
  if (left.length !== right.length) return false;
  for (let index = 0; index < left.length; index += 1) {
    if (left[index] !== right[index]) return false;
  }
  return true;
}

// Interface para o contexto
interface PermissionContextType {
  permissions: string[];
  hasPermission: (permission: AccessType | string) => boolean;
  hasAnyPermission: (permissions: (AccessType | string)[]) => boolean;
  hasAllPermissions: (permissions: (AccessType | string)[]) => boolean;
  isLoading: boolean;
  refreshPermissions: () => Promise<void>;
  /** Decisão do backend para o Mestti OPS; `null` = sem resposta (fechado). */
  mesttiOpsAccess: MyMesttiOpsAccess | null;
}

// Contexto
const PermissionContext = createContext<PermissionContextType | undefined>(undefined);

// Hook para usar o contexto
export const usePermissions = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions deve ser usado dentro de PermissionProvider');
  }
  return context;
};

/** Variante que não lança fora do `PermissionProvider` (retorna `undefined`). */
export const useOptionalPermissions = () => useContext(PermissionContext);

// Provider do contexto
interface PermissionProviderProps {
  children: ReactNode;
}

export const PermissionProvider: React.FC<PermissionProviderProps> = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const [permissions, setPermissions] = useState<string[]>([]);
  const [mesttiOpsAccess, setMesttiOpsAccess] = useState<MyMesttiOpsAccess | null>(null);
  const [loadedForUserId, setLoadedForUserId] = useState<string | null>(null);
  const loadedForUserIdRef = useRef<string | null>(null);
  const inFlightRef = useRef(false);
  const lastLoadedAtRef = useRef(0);

  const loadPermissions = useCallback(async () => {
    if (!isAuthenticated || !user) {
      setPermissions([]);
      setMesttiOpsAccess(null);
      setLoadedForUserId(null);
      loadedForUserIdRef.current = null;
      lastLoadedAtRef.current = 0;
      inFlightRef.current = false;
      return;
    }

    if (inFlightRef.current) return;
    inFlightRef.current = true;
    const alreadyLoadedForUser = loadedForUserIdRef.current === user.id;

    // Mesmo carregamento (sem polling), mas independente dos grants: erro aqui
    // fecha o OPS (fail-closed) sem afetar as permissões de tela.
    const opsAccessPromise = accessService
      .getMyMesttiOpsAccess()
      .then((access) => setMesttiOpsAccess(access))
      .catch((error) => {
        console.error('[FRONTEND] PermissionProvider - Erro ao carregar acesso ao Otto:', error);
        setMesttiOpsAccess(null);
      });

    try {
      const userAccesses: UserAccess[] = await accessService.getUserAccesses(user.id);
      const activePermissions = userAccesses
        .filter((access: UserAccess) => isActiveUserAccess(access))
        .map((access: UserAccess) => access.access_type);

      setPermissions((prev) => (permissionsAreEqual(prev, activePermissions) ? prev : activePermissions));
      if (loadedForUserIdRef.current !== user.id) {
        setLoadedForUserId(user.id);
      }
      loadedForUserIdRef.current = user.id;
      lastLoadedAtRef.current = Date.now();
    } catch (error) {
      console.error('[FRONTEND] PermissionProvider - Erro ao carregar permissões:', error);
      console.error('[FRONTEND] PermissionProvider - Error details:', error);
      if (!alreadyLoadedForUser) {
        setPermissions([]);
        setLoadedForUserId(user.id);
        loadedForUserIdRef.current = user.id;
      }
    } finally {
      await opsAccessPromise;
      inFlightRef.current = false;
    }
  }, [isAuthenticated, user]);

  const hasPermission = (permission: AccessType | string): boolean => {
    return permissions.includes(permission);
  };

  const hasAnyPermission = (permissionsToCheck: (AccessType | string)[]): boolean => {
    return permissionsToCheck.some((permission) => hasPermission(permission));
  };

  const hasAllPermissions = (permissionsToCheck: (AccessType | string)[]): boolean => {
    return permissionsToCheck.every((permission) => hasPermission(permission));
  };

  const refreshPermissions = async () => {
    await loadPermissions();
  };

  useEffect(() => {
    try {
      void loadPermissions();
    } catch (error) {
      console.error('❌ PermissionProvider - error in useEffect:', error);
      setPermissions([]);
      setLoadedForUserId(user?.id ?? null);
      loadedForUserIdRef.current = user?.id ?? null;
    }
  }, [loadPermissions, user?.id]);

  // Revalida concessões em background ao voltar para a janela.
  // Não pode marcar isLoading: o App desmonta todas as rotas enquanto permissões "carregam".
  useEffect(() => {
    if (!isAuthenticated || !user?.id) return undefined;

    const refreshOnFocus = () => {
      if (Date.now() - lastLoadedAtRef.current < FOCUS_REFRESH_MIN_INTERVAL_MS) return;
      void loadPermissions();
    };

    window.addEventListener('focus', refreshOnFocus);
    return () => window.removeEventListener('focus', refreshOnFocus);
  }, [isAuthenticated, loadPermissions, user?.id]);

  const isLoading = Boolean(user?.id && isAuthenticated && loadedForUserId !== user.id);

  const value: PermissionContextType = {
    permissions,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    isLoading,
    refreshPermissions,
    mesttiOpsAccess,
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};
